// PageEditorInsertButton.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './PageEditorInsertButton.css';

// Icons
import Icon from 'svg/Icon';

/**
 * PageEditorInsertButton
 *
 * Renders the thin inline insertion control used between layout items.
 *
 * @param {boolean} isDropActive - Whether the control is the active drag target.
 * @param {function} onClick - Called when the insert button is pressed.
 * @param {function} onDragOver - Drag over handler for insert-target drops.
 * @param {function} onDrop - Drop handler for insert-target drops.
 * @param {string} orientation - Whether the control is horizontal or vertical.
 *
 * @returns {ReactNode} The rendered insert button.
 */
const PageEditorInsertButton = ({
    isDropActive = false,
    onClick,
    onDragOver,
    onDrop,
    orientation = 'vertical',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [isHovered, setIsHovered] = useState(false);

    const resolvedOrientation = orientation === 'horizontal' ? 'horizontal' : 'vertical';
    const isEmphasized = isDropActive || isHovered;

    /**
     * Gets the title for the insert button based on its orientation.
     *
     * @param {string} orientation - The orientation of the insert button.
     *
     * @returns {string} The title for the insert button.
     */
    function getInsertButtonTitle(orientation) {
        return orientation === 'horizontal' ? 'Add column' : 'Add row';
    }

    // ----- RENDER ----- //

    return (
        <div
          className={[
                'layout-editor-insert-button-container',
                resolvedOrientation === 'horizontal'
                    ? 'layout-editor-insert-button-container-horizontal'
                    : 'layout-editor-insert-button-container-vertical',
            ].join(' ')}
          style={{
                backgroundColor: isEmphasized ? theme.buttonHoverBackgroundColor : 'transparent',
                borderColor: isDropActive ? theme.outline : isHovered ? theme.outlineMuted : 'transparent',
                //borderWidth: isEmphasized ? '1px' : '0px',
            }}
          title={getInsertButtonTitle(resolvedOrientation)}
          onClick={(event) => {
                event.stopPropagation();
                onClick?.();
            }}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onMouseDown={(event) => {
                event.stopPropagation();
            }}
          onMouseEnter={() => {
                setIsHovered(true);
            }}
          onMouseLeave={() => {
                setIsHovered(false);
            }}
        >
            <Icon
              color={isEmphasized ? theme.buttonHoverLabelColor : theme.foregroundMuted}
              height="16px"
              iconKey="add"
              width="16px"
            />
        </div>
    );
};

// Dev Tools
PageEditorInsertButton.displayName = 'PageEditorInsertButton';

export default PageEditorInsertButton;
